/**
 * One rule set per field, read by both the form and the submit guard.
 *
 * The two used to disagree: a form could accept a figure the submit path then
 * refused (or worse, sent). Here the rules live once, return a typed issue code
 * rather than a sentence, and the UI maps that code to wording.
 */
import { Dec, dec } from "./decimal";
import { MaxResult } from "./max/types";
import { NumericFieldSpec } from "./spec";

export type NumericIssueCode =
  | "required"
  | "invalid_number"
  | "too_many_decimals"
  | "not_positive"
  | "below_min"
  | "above_max"
  | "below_min_order"
  | "above_max_order"
  | "insufficient_balance"
  | "insufficient_buying_power"
  | "exceeds_position"
  | "exceeds_withdrawable"
  | "max_unknown";

export interface NumericIssue {
  code: NumericIssueCode;
  /** Interpolated into the message: the limit that was crossed, the unit. */
  params?: Record<string, string>;
}

export interface RuleCtx {
  spec: NumericFieldSpec;
  /** The field's ceiling right now; absent when nothing caps it. */
  max?: MaxResult;
  /** Whether an empty field is an issue. Defaults to true. */
  required?: boolean;
}

export type NumericRule = (value: Dec, ctx: RuleCtx) => NumericIssue | null;

const issue = (code: NumericIssueCode, params?: Record<string, string>): NumericIssue => ({
  code,
  params,
});

/** Format, decimals, positivity and the spec's own floor and ceiling. */
export const baseRules: NumericRule[] = [
  (value, { spec }) =>
    (value.split(".")[1]?.length ?? 0) > spec.maxDecimals
      ? issue("too_many_decimals", { decimals: String(spec.maxDecimals) })
      : null,
  (value) => (dec.gt(value, "0") ? null : issue("not_positive")),
  (value, { spec }) =>
    spec.min !== undefined && dec.lt(value, spec.min)
      ? issue(spec.kind === "size" ? "below_min_order" : "below_min", {
          min: spec.min,
          unit: spec.unit,
        })
      : null,
  (value, { spec }) =>
    spec.max !== undefined && dec.gt(value, spec.max)
      ? issue(spec.kind === "size" ? "above_max_order" : "above_max", {
          max: spec.max,
          unit: spec.unit,
        })
      : null,
];

/**
 * The value must fit inside the resolved maximum. An unknown maximum is an
 * issue in its own right: nothing can be checked against it.
 */
export const capRule =
  (code: NumericIssueCode = "insufficient_balance"): NumericRule =>
  (value, { max }) => {
    if (!max) return null;
    if (!max.ok) return issue("max_unknown", { reason: max.reason });
    return dec.gt(value, max.max)
      ? issue(code, { max: max.max, unit: max.unit })
      : null;
  };

/** The first issue with `text`, or `null` when it is acceptable. */
export const validateNumeric = (
  text: string,
  ctx: RuleCtx,
  extra: NumericRule[] = []
): NumericIssue | null => {
  const raw = String(text ?? "").trim();
  if (raw === "") return ctx.required === false ? null : issue("required");
  const value = dec.parse(raw);
  if (value === null) return issue("invalid_number");
  for (const rule of [...baseRules, ...extra]) {
    const found = rule(value, ctx);
    if (found) return found;
  }
  return null;
};

/**
 * react-hook-form adapter: returns `true` or the issue code, so the field's
 * error type is the code and the component picks the wording.
 */
export const toRhfValidate =
  (getCtx: () => RuleCtx, extra: NumericRule[] = []) =>
  (value: string): true | string => {
    const found = validateNumeric(value, getCtx(), extra);
    return found ? found.code : true;
  };

export class NumericValidationError extends Error {
  readonly issue: NumericIssue;

  constructor(found: NumericIssue) {
    super(`Invalid amount: ${found.code}`);
    this.name = "NumericValidationError";
    this.issue = found;
  }
}

/**
 * The submit-time guard. Re-checks against fresh data — a book, a balance or a
 * fee rate can move between the last keystroke and the click — and returns the
 * exact value to send.
 */
export const assertNumeric = (
  text: string,
  ctx: RuleCtx,
  extra: NumericRule[] = []
): Dec => {
  const found = validateNumeric(text, ctx, extra);
  if (found) throw new NumericValidationError(found);
  return dec.parse(text) as Dec;
};
